import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Typography,
    Box,
    useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import { type CommunicationRecord } from '@entities';
import { communicationRecordRepo } from '@repositories';
import useQuestion from '../../dialog-components/QuestionDialog';
import { useUser } from '../../contexts/UserProvider';
import { communicationChannelLabel } from '@shared/helpers/communication-channel';

export const ViewRecordDialog: React.FC<{
    record: CommunicationRecord | null;
    setSelectedRecord: React.Dispatch<React.SetStateAction<CommunicationRecord | null>>;
    onClose: () => void;
    onSave: () => void;
}> = ({ record, onClose, onSave, setSelectedRecord }) => {
    const theme = useTheme();
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const { currentUser } = useUser();
    const question = useQuestion();

    useEffect(() => {
        if (record) {
            setEditTitle(record.title ?? "");
            setEditDescription(record.description ?? "");
            setIsEditing(false);
        }
    }, [record]);

    const handleSave = async () => {
        if (!record || isSaving) return;
        setIsSaving(true);
        try {
            await communicationRecordRepo.update(record.id, {
                title: editTitle || record.title,
                description: editDescription || record.description,
                managerId: currentUser?.id || record.managerId
            });
            setSelectedRecord(null);
            onSave();
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving record:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!record || isDeleting) return;

        const confirmed = await question(
            "האם למחוק את תיעוד התקשורת?",
            "פעולה זו אינה ניתנת לביטול.",
            { confirmText: "מחיקה", cancelText: "ביטול" },
        );
        if (!confirmed) return;

        setIsDeleting(true);
        try {
            await communicationRecordRepo.delete(record.id);
            setSelectedRecord(null);
            onSave();
            setIsEditing(false);
        } catch (error) {
            console.error('Error deleting record:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const isBusy = isSaving || isDeleting;

    return (
        <Dialog
            open={!!record}
            onClose={() => {
                onClose();
                setIsEditing(false);
            }}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 },
            }}
        >
            {record && (
                <>
                    <DialogTitle
                        sx={{
                            background: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1 }} />
                            פרטי תיעוד
                        </Box>
                        <IconButton
                            onClick={() => {
                                onClose();
                                setIsEditing(false);
                            }}
                            sx={{ color: 'inherit' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginTop: '1rem !important',
                                color: 'text.secondary',
                                '& svg': {
                                    fontSize: '1.2rem',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                                <AccessTimeIcon />
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    {format(new Date(record.createdAt), 'dd/MM/yyyy HH:mm')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                                <PersonIcon />
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    {record.manager?.name || 'משתמש לא זמין'}
                                </Typography>
                            </Box>
                        </Box>
                        {communicationChannelLabel(record.channel) ||
                        record.opportunity?.title ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1 }}
                            >
                                {[
                                    communicationChannelLabel(record.channel),
                                    record.opportunity?.title
                                        ? `משימה: ${record.opportunity.title}`
                                        : "",
                                ]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </Typography>
                        ) : null}
                        {isEditing ? (
                            <>
                                <TextField
                                    fullWidth
                                    label="כותרת"
                                    value={editTitle || ""}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    margin="normal"
                                    sx={{
                                        mt: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                        },
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="תיאור"
                                    value={editDescription || ""}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                    margin="normal"
                                    sx={{
                                        mt: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                        },
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mt: 2,
                                        fontWeight: 'bold',
                                        color: 'text.primary',
                                    }}
                                >
                                    {record.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 2,
                                        lineHeight: 1.8,
                                        whiteSpace: 'pre-wrap',
                                        color: 'text.primary',
                                    }}
                                >
                                    {record.description}
                                </Typography>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                        <Button
                            onClick={handleDelete}
                            variant="outlined"
                            color="error"
                            disabled={isBusy}
                            sx={{ borderRadius: 3 }}
                        >
                            מחיקה
                        </Button>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                onClick={() => {
                                    onClose();
                                    setIsEditing(false);
                                }}
                                variant="outlined"
                                disabled={isBusy}
                                sx={{ borderRadius: 3 }}
                            >
                                ביטול
                            </Button>
                            {isEditing ? (
                                <Button
                                    onClick={handleSave}
                                    variant="contained"
                                    color="primary"
                                    disabled={isBusy}
                                    sx={{ borderRadius: 3 }}
                                >
                                    שמירה
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => setIsEditing(true)}
                                    variant="contained"
                                    color="primary"
                                    disabled={isBusy}
                                    sx={{ borderRadius: 3 }}
                                >
                                    עריכה
                                </Button>
                            )}
                        </Box>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};
